import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { UserService } from '@services/user.service';
import { PageService } from '@services/page.service';
import { HttpUtilService } from '@services/http-util.service';
import { environment } from '@env/environment';
import { NotificationsService } from '@services/notifications.service';
import { TranslationService } from '@services/translation.service';
import { CognitoDomainService } from '@services/cognito-domain.service';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { EMPTY, of, Subject, Subscription } from 'rxjs';
import { matchFormControlValidator } from '@app/validators/must-match-input.directive';
import {
  addConfirmPwRevalidation,
  CognitoPasswordPolicy,
  getNewPwValidatorsFromPolicy,
} from '@app/shared/password-form.utils';
@Component({
  selector: 'app-new-password-required',
  templateUrl: './new-password-required.component.html',
  styleUrls: ['./new-password-required.component.scss'],
  standalone: false,
})
export class NewPasswordRequiredComponent implements OnInit, OnDestroy {
  passwordStrength: number = 0;
  currentAttributes: any = {};
  requiredAttributes = [];
  setPassword = false;
  policies = null;
  updatedUserData = {};
  private initialProfileSnapshot: Record<string, unknown> = {};
  hasSSOEnabled: unknown;
  isProcessing = false;

  attributesForm: FormGroup;
  attributeKeys: string[] = [];
  readonly cognitoPhonePlaceholderWhenNotInForm = '+10000000000';
  passwordForm: FormGroup;
  destroy$ = new Subject();
  private confirmPwRevalidationSub?: Subscription;

  /** Built once in ngOnInit; keyed by Cognito attribute name. */
  readonly attributeDisplayNames: Record<string, string> = {};

  constructor(
    private notifications: NotificationsService,
    private userService: UserService,
    private router: Router,
    private page: PageService,
    public location: Location,
    private http: HttpUtilService,
    private translationService: TranslationService,
    private cognitoDomainService: CognitoDomainService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    Object.assign(this.attributeDisplayNames, {
      given_name: this.translationService.getTranslatedContent(
        'first_name',
        'First Name'
      ),
      first_name: this.translationService.getTranslatedContent(
        'first_name',
        'First Name'
      ),
      family_name: this.translationService.getTranslatedContent(
        'last_name',
        'Last Name'
      ),
      last_name: this.translationService.getTranslatedContent(
        'last_name',
        'Last Name'
      ),
      email: this.translationService.getTranslatedContent('email', 'Email'),
    });
    this.page.updateHideMenu(true);
    this.cognitoDomainService
      .getCognitoSsoUrl$()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(cognitoSsoUrl => {
          this.hasSSOEnabled = !!cognitoSsoUrl;
          const userTmp = this.userService.getUserAttributes();
          if (
            !userTmp ||
            !userTmp.currentAttributes ||
            !userTmp.requiredAttributes
          ) {
            this.router.navigate(['/login']);
            return EMPTY;
          }
          this.requiredAttributes = userTmp.requiredAttributes;
          // avoid setting email if sso enabled, it should be set at this point regardless
          this.currentAttributes = !this.hasSSOEnabled
            ? userTmp.currentAttributes
            : Object.keys(userTmp.currentAttributes).reduce((acc, key) => {
                if (key !== 'email') {
                  acc[key] = userTmp.currentAttributes[key];
                }
                return acc;
              }, {});
          this.buildAttributesForm();
          return this.userService.getPasswordPolicy().pipe(
            catchError(err => {
              console.error('Error loading password policy:', err);
              return of({} as CognitoPasswordPolicy);
            })
          );
        })
      )
      .subscribe(policies => {
        this.policies = policies;
        this.buildPasswordForm();
      });
  }

  ngOnDestroy() {
    this.confirmPwRevalidationSub?.unsubscribe();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private displayableAttributeKeys = ['email', 'given_name', 'family_name'];

  private buildAttributesForm() {
    const requiredSet = new Set(
      Array.isArray(this.requiredAttributes) ? this.requiredAttributes : []
    );
    this.displayableAttributeKeys.forEach(k => {
      if (this.hasSSOEnabled && k === 'email') return;
      if (requiredSet.has(k) && !this.currentAttributes.hasOwnProperty(k)) {
        this.currentAttributes[k] = '';
      }
    });
    if (requiredSet.has('phone_number')) {
      const v = this.currentAttributes['phone_number'];
      if (
        v === undefined ||
        v === null ||
        (typeof v === 'string' && v.trim() === '')
      ) {
        this.currentAttributes['phone_number'] =
          this.cognitoPhonePlaceholderWhenNotInForm;
      }
    }
    const keys = this.displayableAttributeKeys.filter(k => {
      if (this.hasSSOEnabled && k === 'email') return false;
      return this.currentAttributes.hasOwnProperty(k) || requiredSet.has(k);
    });
    this.attributeKeys = keys;
    const group: Record<string, unknown> = {};
    keys.forEach(k => {
      const validators = [];
      if (requiredSet.has(k)) {
        validators.push(Validators.required);
      }
      if (k === 'email' && !this.hasSSOEnabled) {
        validators.push(Validators.email);
        if (!requiredSet.has(k)) {
          validators.push(Validators.required);
        }
      }
      group[k] = [this.currentAttributes[k] ?? '', validators];
    });
    this.attributesForm = this.fb.group(group);
    this.captureInitialProfileSnapshot();
    keys.forEach(attr => {
      this.attributesForm
        .get(attr)
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          Object.assign(
            this.currentAttributes,
            this.attributesForm.getRawValue()
          );
        });
    });
  }

  private buildPasswordForm() {
    if (!this.policies) return;
    this.confirmPwRevalidationSub?.unsubscribe();
    const newPwValidators = getNewPwValidatorsFromPolicy(this.policies);
    this.passwordForm = this.fb.group({
      oldPw: ['', Validators.required],
      newPw: ['', newPwValidators],
      confirmPw: [
        '',
        [Validators.required, matchFormControlValidator('newPw')],
      ],
    });
    this.confirmPwRevalidationSub =
      addConfirmPwRevalidation(
        this.passwordForm,
        'newPw',
        'confirmPw',
        this.destroy$
      ) ?? undefined;
  }

  stillRequiredAttributes() {
    if (!this.attributesForm) return true;
    if (!this.attributesForm.valid) return true;
    const required = Array.isArray(this.requiredAttributes)
      ? this.requiredAttributes
      : [];
    const hasMissingRequired = required.some(
      attr =>
        this.currentAttributes[attr] == null ||
        String(this.currentAttributes[attr]).trim() === ''
    );
    return hasMissingRequired;
  }

  private readonly cognitoToAppProfileMap: Record<string, string> = {
    given_name: 'first_name',
    family_name: 'last_name',
    email: 'email',
  };

  private captureInitialProfileSnapshot(): void {
    this.initialProfileSnapshot = {};
    if (!this.attributesForm) return;
    const raw = this.attributesForm.getRawValue() as Record<string, unknown>;
    for (const [cognitoKey, appKey] of Object.entries(
      this.cognitoToAppProfileMap
    )) {
      if (!Object.prototype.hasOwnProperty.call(raw, cognitoKey)) continue;
      if (this.hasSSOEnabled && cognitoKey === 'email') continue;
      this.initialProfileSnapshot[appKey] = raw[cognitoKey];
    }
  }

  private profileValuesEqual(a: unknown, b: unknown): boolean {
    return String(a ?? '').trim() === String(b ?? '').trim();
  }

  private syncProfileDataFromForm(): void {
    if (!this.attributesForm) return;
    const raw = this.attributesForm.getRawValue() as Record<string, unknown>;
    Object.assign(this.currentAttributes, raw);
    const next: Record<string, unknown> = {};
    for (const [cognitoKey, appKey] of Object.entries(
      this.cognitoToAppProfileMap
    )) {
      if (!Object.prototype.hasOwnProperty.call(raw, cognitoKey)) continue;
      if (this.hasSSOEnabled && cognitoKey === 'email') continue;
      const newVal = raw[cognitoKey];
      const initialVal = this.initialProfileSnapshot[appKey];
      if (!this.profileValuesEqual(newVal, initialVal)) {
        next[appKey] = newVal;
      }
    }
    this.updatedUserData = next;
  }

  continueToPasswordStep(): void {
    this.syncProfileDataFromForm();
    this.setPassword = true;
  }

  onConfirmPasswordEnter(event: KeyboardEvent): void {
    event.preventDefault();
    if (!this.passwordForm || this.passwordForm.invalid || this.isProcessing) {
      return;
    }
    this.submitNewPw();
  }

  attributeDisplayName(name: string): string {
    return this.attributeDisplayNames[name] ?? name;
  }

  private readonly writableAttributeKeys = [
    'given_name',
    'family_name',
    'email',
    'phone_number',
    'picture',
  ];

  backFromPasswordStep(): void {
    this.passwordForm.patchValue({ newPw: '', confirmPw: '' });
    ['newPw', 'confirmPw'].forEach(key => {
      const c = this.passwordForm.get(key);
      c?.markAsPristine();
      c?.markAsUntouched();
      c?.updateValueAndValidity();
    });
    this.passwordStrength = 0;
    this.setPassword = false;
  }

  submitNewPw() {
    this.isProcessing = true;
    this.syncProfileDataFromForm();
    const profileUpdateSnapshot: Record<string, unknown> = {
      ...this.updatedUserData,
    };
    const updatedKeys = Object.keys(profileUpdateSnapshot);
    const requiredSet = new Set(
      Array.isArray(this.requiredAttributes) ? this.requiredAttributes : []
    );
    const oldPw = this.passwordForm.get('oldPw').value;
    const newPw = this.passwordForm.get('newPw').value;
    const attributesForCognito = this.getAttributesForCompleteNewPassword(
      this.currentAttributes,
      requiredSet,
      profileUpdateSnapshot
    );
    this.userService
      .completeNewPasswordChallenge(newPw, oldPw, attributesForCognito)
      .subscribe({
        next: () => {
          if (updatedKeys.length > 0) {
            const payload = this.buildUsersSelfPayload(profileUpdateSnapshot);
            this.http
              .request(
                'PUT',
                environment.apiRoot + '/users/self',
                { data: payload },
                null,
                true
              )
              .subscribe();
          }

          this.isProcessing = false;
          this.router.navigate(['/verify-phone-and-email']);
        },
        error: err => {
          this.isProcessing = false;
          const message = this.getErrorMessageFromCompleteNewPassword(err);
          this.notifications.popNotification('Error', message, 5);
        },
      });
  }
  private buildUsersSelfPayload(
    data: Record<string, unknown>
  ): Record<string, unknown> {
    const payload = { ...data };
    const raw = payload['phone_number'];
    if (raw !== undefined && raw !== null) {
      payload['phone_number'] =
        typeof raw === 'object' && 'e164Number' in raw
          ? raw
          : { e164Number: typeof raw === 'string' ? raw : String(raw) };
    }
    return payload;
  }

  private getAttributesForCompleteNewPassword(
    current: Record<string, unknown>,
    requiredSet: Set<string>,
    userModifiedData: Record<string, unknown>
  ): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const verifiableAttrs = ['email', 'phone_number'];
    for (const key of this.writableAttributeKeys) {
      const value = current[key];
      const isEmptyString =
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '');

      if (
        verifiableAttrs.includes(key) &&
        !Object.prototype.hasOwnProperty.call(userModifiedData, key) &&
        !requiredSet.has(key)
      ) {
        continue;
      }
      if (requiredSet.has(key)) {
        out[key] = value ?? '';
        continue;
      }
      if (isEmptyString || !current.hasOwnProperty(key)) continue;
      out[key] = value;
    }
    return out;
  }

  private getErrorMessageFromCompleteNewPassword(
    err: string | { error?: string; message?: string }
  ): string {
    const code = typeof err === 'string' ? err : (err?.error ?? err?.message);
    if (code === 'InvalidParameterException') {
      return this.translationService.getTranslatedContent(
        'invalid_profile_attributes',
        'One or more profile fields are invalid or not allowed. Please check and try again.'
      );
    }
    if (code === 'NotAuthorizedException') {
      return this.translationService.getTranslatedContent(
        'incorrect_current_password',
        'Incorrect current password. Please check and try again.'
      );
    }
    return this.translationService.getTranslatedContent(
      'error_validating_new_password',
      'Error Validating New Password'
    );
  }
}
